import { Lightbulb } from "lucide-react";
import { useEffect, useState } from "react";

import { useT } from "@/i18n";
import { draftsApi, type AiExecution } from "@/lib/builder-api";

interface ExecutionsTableProps {
  /** Report draft key — the report whose insight executions to list. */
  draftKey: string;
}

/**
 * Read-only list of a report's persisted AI-insight executions
 * (``ai_analysis_snapshots`` rows, owned + written by the portal). Shown
 * inside the editor's "Insights AI" tab so the admin can see when each
 * insight ran, on which period, with what model, and how long it took.
 * No write actions — regenerate/backfill stay the portal's surface.
 */
export function ExecutionsTable({ draftKey }: ExecutionsTableProps): JSX.Element {
  const { t } = useT();
  const [rows, setRows] = useState<AiExecution[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const data = await draftsApi.aiExecutions(draftKey);
        if (cancelled) return;
        setRows(data.executions);
        setTotal(data.total);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [draftKey]);

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
      <header className="mb-3 flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-primary-dark" />
        <h2 className="text-base font-semibold text-neutral-900">
          {t("editor.ai.executions.title")}
          {total > 0 ? (
            <span className="ml-2 text-xs font-normal text-neutral-500">
              ({total})
            </span>
          ) : null}
        </h2>
      </header>
      <p className="mb-4 text-xs text-neutral-500">
        {t("editor.ai.executions.description")}
      </p>

      {loading ? (
        <p className="text-xs text-neutral-500">
          {t("editor.ai.executions.loading")}
        </p>
      ) : error ? (
        <p className="text-xs text-error-dark">
          {t("editor.ai.executions.error")}: {error}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-neutral-500">
          {t("editor.ai.executions.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-neutral-500">
              <tr className="border-b border-neutral-200">
                <th className="px-2 py-1 text-left">
                  {t("editor.ai.executions.col.period")}
                </th>
                <th className="px-2 py-1 text-left">
                  {t("editor.ai.executions.col.model")}
                </th>
                <th className="px-2 py-1 text-right">
                  {t("editor.ai.executions.col.duration")}
                </th>
                <th className="px-2 py-1 text-right">
                  {t("editor.ai.executions.col.insights")}
                </th>
                <th className="px-2 py-1 text-right">
                  {t("editor.ai.executions.col.regenerated")}
                </th>
                <th className="px-2 py-1 text-left">
                  {t("editor.ai.executions.col.created")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100">
                  <td className="px-2 py-1 text-neutral-700">{r.as_of_date}</td>
                  <td className="px-2 py-1 text-neutral-600">{r.model}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-neutral-600">
                    {formatMs(r.duration_ms)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-neutral-600">
                    {r.insights_count}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-neutral-600">
                    {r.regenerated_count}
                  </td>
                  <td className="px-2 py-1 text-neutral-600">
                    {parseAsUTC(r.created_at)?.toLocaleString() ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Format a duration in milliseconds; ``null`` → em-dash. */
function formatMs(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

/**
 * Parse a server timestamp as UTC. The portal stores naive-UTC datetimes
 * (no tz suffix); without forcing ``Z`` the browser reads them as local
 * time, shifting by the user's offset. Mirrors the loader UI's helper.
 */
function parseAsUTC(iso: string | null): Date | null {
  if (!iso) return null;
  const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : iso + "Z");
}
